import * as joint from 'jointjs';
import $ from 'jquery';
import { JointCommentShape } from '../Repository/Shape/JointCommentShape';
import { JointProductAPPShape } from '../Repository/Shape/JointProductAPPShape';
import { JointReferenceEntityShape } from '../Repository/Shape/JointReferenceEntityShape';
import { PartShape } from './PartShape';

export enum ResizeDirection {
  /** 左右方向 */
  LeftRight = 1,
  /** 四角方向 */
  FourHorns = 2,
  /** 所有八方向：四角+上下左右 */
  All = 16
}

export class ResizeShape extends joint.dia.Element {
  constructor(direction: ResizeDirection) {
    const opt = {
      type: 'mst.ResizeShape',
      markup: [] as any[],
      attrs: {
        right: { refX: '100%', refY: '50%', event: 'resize:right', cursor: 'w-resize', fill: '#fff', stroke: '#000', x: -3, y: -3, width: 7, height: 7 },
        left: { refY: '50%', event: 'resize:left', cursor: 'w-resize', fill: '#fff', stroke: '#000', x: -3, y: -3, width: 7, height: 7 },
        topLeft: { event: 'resize:topLeft', cursor: 'nwse-resize', fill: '#fff', stroke: '#000', x: -3, y: -3, width: 7, height: 7 },
        topRight: { refX: '100%', event: 'resize:topRight', cursor: 'nesw-resize', fill: '#fff', stroke: '#000', x: -3, y: -3, width: 7, height: 7 },
        bottomLeft: { refY: '100%', event: 'resize:bottomLeft', cursor: 'nesw-resize', fill: '#fff', stroke: '#000', x: -3, y: -3, width: 7, height: 7 },
        bottomRight: { refX: '100%', refY: '100%', event: 'resize:bottomRight', cursor: 'nwse-resize', fill: '#fff', stroke: '#000', x: -3, y: -3, width: 7, height: 7 }
      }
    };
    if (direction === ResizeDirection.FourHorns) {
      opt.markup.push({ tagName: 'rect', selector: 'topLeft' },
        { tagName: 'rect', selector: 'topRight' },
        { tagName: 'rect', selector: 'bottomLeft' },
        { tagName: 'rect', selector: 'bottomRight' });
    } else if (direction === ResizeDirection.LeftRight) {
      opt.markup.push({ tagName: 'rect', selector: 'left' },
        { tagName: 'rect', selector: 'right' });
    } else if (direction === ResizeDirection.All) {
      opt.markup.push({ tagName: 'rect', selector: 'left' },
        { tagName: 'rect', selector: 'right' },
        { tagName: 'rect', selector: 'topLeft' },
        { tagName: 'rect', selector: 'topRight' },
        { tagName: 'rect', selector: 'bottomLeft' },
        { tagName: 'rect', selector: 'bottomRight' });
    }
    super(opt);
  }
}

export class ResizeTool {
  private paper: any = null;
  private resizeElement: any = null;
  private bindElement: any = null;

  /**
   * 构造改变大小的工具实例
   * @param paper 画板
   */
  constructor(paper: any) {
    this.paper = paper;

    // 事件响应
    paper.on('resize:left', (elementView: any, evt: any) => {
      evt.stopPropagation();
      this.drag(evt.offsetX, evt.offsetY, 1, 0, -1, 0);
    });
    paper.on('resize:right', (elementView: any, evt: any) => {
      evt.stopPropagation();
      this.drag(evt.offsetX, evt.offsetY, 0, 0, 1, 0);
    });
    paper.on('resize:topLeft', (elementView: any, evt: any) => {
      evt.stopPropagation();
      this.drag(evt.offsetX, evt.offsetY, 1, 1, -1, -1);
    });
    paper.on('resize:bottomRight', (elementView: any, evt: any) => {
      evt.stopPropagation();
      this.drag(evt.offsetX, evt.offsetY, 0, 0, 1, 1);
    });
    paper.on('resize:topRight', (elementView: any, evt: any) => {
      evt.stopPropagation();
      this.drag(evt.offsetX, evt.offsetY, 0, 1, 1, -1);
    });
    paper.on('resize:bottomLeft', (elementView: any, evt: any) => {
      evt.stopPropagation();
      this.drag(evt.offsetX, evt.offsetY, 1, 0, -1, 1);
    });

    paper.on('element:pointerclick', (cellView: any, evt: any, x: number, y: number) => {
      // evt.stopPropagation();
      let direction = ResizeDirection.FourHorns;
      if (cellView.model instanceof PartShape || cellView.model instanceof JointProductAPPShape || cellView.model instanceof JointReferenceEntityShape) {
        direction = ResizeDirection.LeftRight;
      } else if (cellView.model instanceof JointCommentShape) {
        direction = ResizeDirection.FourHorns;
      }
      this.bind(cellView.model, direction);
    });
    paper.on('blank:pointerclick', (evt: any, x: number, y: number) => {
      this.unbind();
    });
  }

  // 绑定元素
  bind(element: any, direction: ResizeDirection) {
    // 如果点击自己，则返回
    if (element.attributes.type === 'mst.ResizeShape') return;
    // 释放上一个绑定
    this.unbind();

    // 绑定元素
    this.bindElement = element;
    this.resizeElement = (new ResizeShape(direction))
      .size(element.attributes.size.width, element.attributes.size.height)
      .position(element.attributes.position.x, element.attributes.position.y)
      .addTo(this.paper.model);

    // 让绑定元素位置和大小同步
    this.resizeElement.on('change:position', (element: any, position: any) => {
      this.bindElement.position(position.x, position.y);
    });
    this.resizeElement.on('change:size', (element: any, size: any) => {
      this.bindElement.resize(size.width, size.height);
      this.bindElement.fitToSize(size.width, size.height);
    });
  }

  // 拖动元素尺寸
  drag(startX: number, startY: number, xDir: number, yDir: number, wDir: number, hDir: number) {
    // 记录初始坐标
    const elementX = this.resizeElement.attributes.position.x;
    const elementY = this.resizeElement.attributes.position.y;

    const MIN_SIZE_WIDTH = 150; // 最小尺寸：宽
    const MIN_SIZE_HEIGHT = 60; // 最小尺寸：高
    const elementWidth = this.resizeElement.attributes.size.width;
    const elementHeight = this.resizeElement.attributes.size.height;
    $(this.paper.el).on('mousemove', (event) => {
      // 计算移动量
      const dx = event.offsetX - startX;
      const dy = event.offsetY - startY;

      // 计算新坐标
      let newX = elementX + dx * xDir;
      let newY = elementY + dy * yDir;
      newX = (elementX + elementWidth) - newX >= MIN_SIZE_WIDTH ? newX : (elementX + elementWidth) - MIN_SIZE_WIDTH;
      newY = (elementY + elementHeight) - newY >= MIN_SIZE_HEIGHT ? newY : (elementY + elementHeight) - MIN_SIZE_HEIGHT;

      // 计算新尺寸
      let newWidth = elementWidth + dx * wDir;
      let newHeight = elementHeight + dy * hDir;
      newWidth = newWidth >= MIN_SIZE_WIDTH ? newWidth : MIN_SIZE_WIDTH;
      newHeight = newHeight >= MIN_SIZE_HEIGHT ? newHeight : MIN_SIZE_HEIGHT;

      // 移动元素
      this.resizeElement.position(newX, newY);
      this.resizeElement.resize(newWidth, newHeight);
    });
    $('body').on('mouseup', (event: any) => {
      $(this.paper.el).off('mousemove');
      $('body').off('mouseup');
    });
  }

  // 释放元素
  unbind() {
    if (this.resizeElement) {
      this.resizeElement.remove();
      this.resizeElement = null;
    }
  }
}
